/**
 * StatsService - Statistics collection and monitoring
 */

import { eventBus } from './EventBus';
import type { StreamStats, ServerStats } from '../types';
import { STATS_POLL_INTERVAL_MS } from '../constants';
import { formatHostRelay } from '../utils/relayDisplay';

class StatsService {
  private pc: RTCPeerConnection | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private pollTimer: number | null = null;
  private isPolling = false;
  private isEnabled = true;  // 控制是否进行统计收集

  // Jitter buffer monitoring
  private lastCatchupTime = 0;
  private smoothedJitterBuffer = 0; // Smoothed jitter buffer for stable decisions
  private consecutiveHighCount = 0; // Count of consecutive high jitter readings
  private staleJitterTicks = 0; // 连续无新解码帧的 tick 数，用于衰减抖动缓冲值避免冻结

  // Packet loss monitoring for auto TURN switch
  private packetLossStartTime = 0; // 丢包开始时间
  private connectionStartTime = 0; // 连接开始时间 (for grace period)
  private readonly PACKET_LOSS_THRESHOLD = 10; // 丢包率阈值 (%)
  private readonly PACKET_LOSS_DURATION_MS = 10000; // 持续丢包时间阈值 (10秒)
  private readonly INIT_GRACE_PERIOD_MS = 10000; // 初始化宽限期 (10秒) - 初始丢包正常，跳过检测

  // 高延迟告警节流：要求连续多次超阈值(过滤瞬时抖动) + 冷却(避免每秒刷屏)
  private highLatencyStreak = 0;            // 连续超阈值的 poll 次数
  private lastHighLatencyWarnAt = 0;        // 上次高延迟告警时间戳
  private readonly HIGH_LATENCY_THRESHOLD_MS = 200;
  private readonly HIGH_LATENCY_STREAK = 3;        // 连续 3 次(≈3秒)超阈值才告警
  private readonly HIGH_LATENCY_COOLDOWN_MS = 30000; // 告警后 30 秒内不重复弹

  // 统一「视频卡死」检测（B0 中枢）：fps=0 持续过久时 emit 'video:stuck'，由 WebRTCService.handleVideoStuck 按 reason 分流
  // 触发后重连会导致 Unsubscribe → subscribers_ 空 → 下次 Subscribe 清零 host 端 smoothed_bitrate_kbps_
  private zeroFpsStartTime = 0; // 接收帧率 = 0 开始时间
  private hasEverDecodedFrame_ = false;                       // 本连接是否曾解码出帧（framesDecoded>0 即永久 true）
  private bsLastFramesReceived_ = 0;                          // 上次采样 framesReceived（判“有数据到达”）
  private lastFirstFrameReconnectTime_ = 0;                   // 首连 never-decode 重连冷却（与 stall 拆开）
  private lastStallReconnectTime_ = 0;                        // 已出帧后卡死重连冷却
  private lowPowerMode_ = false;                              // isEnabled=false 时进入低功耗采样（模式切换打日志）
  private readonly FIRST_FRAME_GRACE_PERIOD_MS = 8000;        // 可调（按 ICE连通→首帧解码 p95 定，见 OQ-1）
  private readonly FIRST_FRAME_STALL_DURATION_MS = 5000;      // 可调
  private readonly FIRST_FRAME_RECONNECT_COOLDOWN_MS = 20000; // 可调（首连失败二次重试更快，不压制 stall 检测）
  private readonly ZERO_FPS_DURATION_MS = 15000; // 已出帧后：fps=0 持续 15 秒触发
  private readonly RECONNECT_COOLDOWN_MS = 60000; // 已出帧后：两次自动重连最小间隔 60 秒
  private readonly DEADLOCK_GRACE_PERIOD_MS = 30000; // 已出帧后：连接建立后 30 秒内不检测（首帧可能较慢）

  // Stats history for charts
  // Stage 3D: 扩展到 5 min × 1Hz = 300 点（之前 60s）+ 加 QP/encode_ms/capture_lag/utilization
  // 提供 Dashboard MainTimeline 完整 5 min 时间窗
  private history: {
    timestamps: number[];
    fps: number[];
    bitrate: number[];
    latency: number[];
    packetLoss: number[];
    jitterBuffer: number[];
    rtt: number[];
    hostCaptureFrames: number[];
    hostEncodeFrames: number[];
    hostDeliveredFrames: number[];
    // Stage 3D 新增字段
    qp: number[];                // dominant engine encoder_qp（-1 表 N/A）
    encodeMs: number[];          // latencyBreakdown.encode
    captureLagMs: number[];      // latencyBreakdown.capture
    utilizationPct: number[];    // actual_encode_kbps / bitrate_kbps × 100
  } = {
    timestamps: [],
    fps: [],
    bitrate: [],
    latency: [],
    packetLoss: [],
    jitterBuffer: [],
    rtt: [],
    hostCaptureFrames: [],
    hostEncodeFrames: [],
    hostDeliveredFrames: [],
    qp: [],
    encodeMs: [],
    captureLagMs: [],
    utilizationPct: [],
  };

  // Previous values for delta calculations
  private prevStats: {
    bytesReceived: number;
    framesDecoded: number;
    timestamp: number;
    jitterBufferDelay: number;
    jitterBufferEmittedCount: number;
    packetsLost: number;
    packetsReceived: number;
    totalDecodeTime: number;
  } = {
    bytesReceived: 0,
    framesDecoded: 0,
    timestamp: 0,
    jitterBufferDelay: 0,
    jitterBufferEmittedCount: 0,
    packetsLost: 0,
    packetsReceived: 0,
    totalDecodeTime: 0,
  };

  // 独立的 fps/bitrate 累计差分基准点。
  // 与 prevStats 解耦：只在"有效采样"(timeDelta>=阈值 且 framesDelta>0) 时才前移，
  // 避免 WebRTC getStats() 对 inbound-rtp 的节流窗口导致 framesDelta=0 被误算成 fps=0。
  // 当一次 poll 落在节流窗口内时，本次不前移基准点，也不覆盖 _currentStats.fps/bitrate，
  // 下一次有效采样会把"欠"的帧自然补回来。
  private fpsSample: {
    framesDecoded: number;
    bytesReceived: number;
    timestamp: number;
  } = {
    framesDecoded: 0,
    bytesReceived: 0,
    timestamp: 0,
  };

  private _currentStats: StreamStats = this.getEmptyStats();
  private _serverStats: ServerStats | null = null;
  private _knownTurnProtocol: 'TCP' | 'UDP' | null = null;  // From ICE Racing config

  // 阶段 2：编码器 catalog 独立存放（不进 _serverStats，避免 server_stats 大消息 merge 时被覆盖/丢失）
  // get 方法返回快照，组件可订阅 'stats:encoderCatalog' 事件
  private _encoderCatalog: {
    catalog: import('../types').EncoderCatalogEntry[];
    active_encoder_id: import('../types').EncoderId | 'none';
    probe_pending: boolean;
  } | null = null;

  // ===== 30s 事件窗口（Stage 1）=====
  // host 推累计值（events_lifetime / events_total 兼容别名），前端做差值得到 30s 增量
  // 保留最近 35 秒的 snapshot
  private _eventsHistory: Array<{ ts: number; events: NonNullable<ServerStats['events_total']> }> = [];

  get currentStats(): StreamStats {
    return { ...this._currentStats };
  }

  // 当前 stats 轮询绑定的 PC。供 WebRTCService 关键帧周期重发判断 baseline 是否属于当前连接
  //（B5 reviewer 修正 (b)：TCP 路径 ontrack 早于 CONNECTED，stats 未绑定 winner PC 时 currentStats 是上一连接残留值）
  get attachedPc(): RTCPeerConnection | null {
    return this.pc;
  }

  // Set the known TURN protocol from ICE Racing configuration
  setKnownTurnProtocol(protocol: 'TCP' | 'UDP' | null): void {
    this._knownTurnProtocol = protocol;
    if (protocol) {
    }
  }

  // relayServer 现在从 WebRTC stats 的 local-candidate url 字段动态获取
  // 不再需要外部设置

  get serverStats(): ServerStats | null {
    if (!this._serverStats && !this._encoderCatalog) return null;
    // 把独立保存的 encoder catalog 合并到返回的 serverStats 快照里（不修改 _serverStats）
    const base = (this._serverStats ? { ...this._serverStats } : {}) as ServerStats;
    if (this._encoderCatalog) {
      base.encoder_catalog = this._encoderCatalog.catalog;
      base.active_encoder_id = this._encoderCatalog.active_encoder_id;
      base.probe_pending = this._encoderCatalog.probe_pending;
    }
    return base;
  }

  // 给组件直接读 catalog（不经 serverStats）
  get encoderCatalog() {
    return this._encoderCatalog;
  }

  get statsHistory() {
    return { ...this.history };
  }

  // Bound handler for server stats via DataChannel
  private boundHandleServerStats = this.handleServerStats.bind(this);
  private boundHandleServerCaps = this.handleServerCaps.bind(this);
  private boundHandleHostVersion = this.handleHostVersion.bind(this);
  private boundHandlePcSwitched = this.handlePcSwitched.bind(this);
  // 阶段 2：编码器 catalog 消息
  private boundHandleEncoderCatalog = this.handleEncoderCatalog.bind(this);

  /**
   * Start polling stats
   * @param pc RTCPeerConnection
   * @param serverWsUrl WebSocket URL (ws://host:port/ws) - no longer used for HTTP
   * @param videoElement HTMLVideoElement for jitter buffer catchup
   */
  start(pc: RTCPeerConnection, serverWsUrl?: string, videoElement?: HTMLVideoElement): void {
    this.stop(); // Clear any existing polling first
    this.pc = pc; // Then set pc
    this.videoElement = videoElement || null;
    this.lastCatchupTime = 0;

    // Reset stats on new connection (preserve TURN protocol from racing config)
    const savedKnownTurnProtocol = this._knownTurnProtocol;
    this._currentStats = this.getEmptyStats();
    this._knownTurnProtocol = savedKnownTurnProtocol;
    this._serverStats = null;
    this._eventsHistory = [];  // 重置事件窗口（连接重启时累计值会归零，避免错误差值）
    this.smoothedJitterBuffer = 0;
    this.consecutiveHighCount = 0;
    this.staleJitterTicks = 0;
    this.packetLossStartTime = 0; // 重置丢包计时
    this.zeroFpsStartTime = 0; // 重置死锁计时
    this.hasEverDecodedFrame_ = false;
    this.bsLastFramesReceived_ = 0;
    // 两个冷却时间戳（lastFirstFrameReconnectTime_/lastStallReconnectTime_）跨连接保留不重置，保持冷却语义
    this.connectionStartTime = Date.now(); // 记录连接开始时间
    this.prevStats = {
      bytesReceived: 0,
      framesDecoded: 0,
      timestamp: 0,
      jitterBufferDelay: 0,
      jitterBufferEmittedCount: 0,
      packetsLost: 0,
      packetsReceived: 0,
      totalDecodeTime: 0,
    };
    this.fpsSample = { framesDecoded: 0, bytesReceived: 0, timestamp: 0 };

    // Server stats are now received via DataChannel, not HTTP
    // Register event listener for server stats
    // 改用 stats:rawServerStats 接收 WebRTCService 转发的原始 data.stats，
    // 由本 service 统一处理 + 合并 catalog 后 re-emit 'stats:serverStats' 给 UI。
    // 防止 server_stats（不含 catalog） 和 server_caps / server_encoder_catalog（含 catalog）
    // 直接竞争发到 UI，导致 catalog 字段每秒抖动 → SettingsPanel 闪烁
    eventBus.on('stats:rawServerStats', this.boundHandleServerStats);
    eventBus.on('stats:serverCaps', this.boundHandleServerCaps);
    // 阶段 2：server_encoder_catalog 独立小消息（编码器自检结果 + 当前 active id）
    eventBus.on('stats:encoderCatalog', this.boundHandleEncoderCatalog);
    // Listen for host version
    eventBus.on('stats:hostVersion', this.boundHandleHostVersion);
    // Listen for PC switch (P2P assist)
    eventBus.on('webrtc:pcSwitched', this.boundHandlePcSwitched);

    this.isPolling = true;
    this.poll();
  }

  /**
   * Stop polling stats
   */
  stop(): void {
    this.isPolling = false;
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.pc = null;
    // Remove listeners
    eventBus.off('stats:rawServerStats', this.boundHandleServerStats);
    eventBus.off('stats:serverCaps', this.boundHandleServerCaps);
    eventBus.off('stats:encoderCatalog', this.boundHandleEncoderCatalog);
    eventBus.off('stats:hostVersion', this.boundHandleHostVersion);
    eventBus.off('webrtc:pcSwitched', this.boundHandlePcSwitched);
  }

  /**
   * Enable or disable stats collection
   * When disabled, polling continues but at a much lower frequency
   * and only collects minimal data for connection health monitoring
   */
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;

    // If we're being enabled and polling was stopped, restart it
    if (enabled && this.pc && !this.pollTimer) {
      this.isPolling = true;
      this.poll();
    }
  }

  /**
   * Poll stats (WebRTC only - server stats come via DataChannel)
   */
  private async poll(): Promise<void> {
    if (!this.isPolling || !this.pc) {
      console.warn('[Stats] Poll skipped: isPolling=', this.isPolling, 'pc=', !!this.pc);
      return;
    }

    try {
      // When disabled, only collect minimal stats for connection health
      if (!this.isEnabled) {
        if (!this.lowPowerMode_) {
          this.lowPowerMode_ = true;
          console.log('[Stats] low-power mode: path quality sampling at 5s interval');
        }
        // Reduced polling - connection health + 路径质量最小集。
        // PathObserver 的丢包劣化检测与 video:stuck 卡死检测不允许因 UI 隐藏而失明，
        // 从同一份 getStats 结果顺手提取，不做 full collection。
        const stats = await this.pc.getStats();
        stats.forEach((report) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            this._currentStats.rtt = Math.round((report.currentRoundTripTime || 0) * 1000);
          }
        });
        this.samplePathQualityLowPower(stats);
        this.emitPathQuality();
        this.checkVideoStuck();

        // Schedule next poll at lower frequency (5 seconds instead of 500ms)
        this.pollTimer = window.setTimeout(() => this.poll(), 5000);
        return;
      }
      if (this.lowPowerMode_) {
        this.lowPowerMode_ = false;
        console.log('[Stats] low-power mode exit: full stats collection resumed');
      }

      // Full stats collection when enabled
      await this.collectWebRTCStats();

      eventBus.emit('stats:update', {
        server: this._serverStats,
        webrtc: this._currentStats,
      });
      this.emitPathQuality();

      // Check for warnings
      this.checkWarnings();

      // Jitter buffer catchup - skip to latest frame if buffer is too high
      this.performCatchup();

      // Send network stats to server for adaptive FPS
      this.sendNetworkStatsToServer();

      // Update history
      this.updateHistory();
    } catch (e) {
      console.error('[Stats] Polling error:', e);
    }

    // Schedule next poll at normal frequency
    this.pollTimer = window.setTimeout(() => this.poll(), STATS_POLL_INTERVAL_MS);
  }

  /**
   * Collect WebRTC stats
   */
  private async collectWebRTCStats(): Promise<void> {
    if (!this.pc) return;

    const stats = await this.pc.getStats();
    const now = Date.now();

    // Variables to track selected candidate pair for transport type detection
    let selectedLocalCandidateId: string | undefined;
    let selectedRemoteCandidateId: string | undefined;

    // Store all candidates for later lookup (iteration order is not guaranteed)
    const localCandidates = new Map<string, { type: string; protocol: string; relayProtocol: string; url: string }>(); // id -> {type, protocol, relayProtocol, url}
    const remoteCandidates = new Map<string, { type: string; address: string; protocol: string }>(); // id -> {type, address, protocol}

    // Store codec info for lookup by codecId
    const codecs = new Map<string, string>(); // id -> mimeType

    // First pass: collect all codec reports
    stats.forEach((report) => {
      if (report.type === 'codec') {
        codecs.set(report.id, report.mimeType || '');
      }
    });

    // Second pass: process other reports
    stats.forEach((report) => {
      if (report.type === 'inbound-rtp' && report.kind === 'video') {
        // Get actual codec from codecId
        if (report.codecId && codecs.has(report.codecId)) {
          const mimeType = codecs.get(report.codecId)!.toLowerCase();
          if (mimeType.includes('h265') || mimeType.includes('hevc')) {
            this._currentStats.videoCodec = 'H265';
          } else if (mimeType.includes('h264') || mimeType.includes('avc')) {
            this._currentStats.videoCodec = 'H264';
          } else if (mimeType.includes('vp9')) {
            this._currentStats.videoCodec = 'VP9';
          } else if (mimeType.includes('vp8')) {
            this._currentStats.videoCodec = 'VP8';
          } else if (mimeType.includes('av1')) {
            this._currentStats.videoCodec = 'AV1';
          }
        }

        // Calculate FPS / bitrate — 累计差分 + 有效采样门限
        //
        // 原因：Chromium 对 inbound-rtp 的 framesDecoded/bytesReceived 有 ~200ms 节流，
        // 当两次 poll 间隔落进节流窗口时，报告里的计数器会返回同一个值，
        // 若直接每次 poll 都前移基准点并覆盖 fps/bitrate，历史曲线会出现
        // "30 → 0 → 30" 的虚假振荡（见诊断报告案例）。
        //
        // 修复思路：基准点 this.fpsSample 只在"有效采样"时才前移。
        // 无效采样（timeDelta 过短 或 framesDelta=0）时：
        //   - 不前移基准点 → 下一次有效采样会把"欠"的帧一并算进来
        //   - 不覆盖 _currentStats.fps/bitrate → 历史曲线复用上一格值（平台而非 0）
        //
        // 重要：此算法仅影响 fps/bitrate，不改数据源——仍然是接收端 framesDecoded。
        // 若接收端真的连续 1+ 秒没解码出帧（真卡顿），framesDelta 始终为 0，
        // 但 timeDelta 会持续累积，下一次有效采样门限触发时会如实算出低 fps，
        // 真实掉帧**不会**被隐藏。
        if (this.fpsSample.timestamp === 0) {
          // 首次采样：只记录基准点，不出 fps
          this.fpsSample.framesDecoded = report.framesDecoded;
          this.fpsSample.bytesReceived = report.bytesReceived;
          this.fpsSample.timestamp = now;
        } else {
          const framesDelta = report.framesDecoded - this.fpsSample.framesDecoded;
          const bytesDelta = report.bytesReceived - this.fpsSample.bytesReceived;
          const timeDelta = (now - this.fpsSample.timestamp) / 1000;

          // 有效采样门限：时间间隔 >= 0.8s 并且至少新出了 1 帧（避开 getStats 节流窗口）
          // 0.8s 略小于 1s 是为了允许 setTimeout 漂移，不至于把正常 ~0.9s 的采样误判掉
          const isValidSample = timeDelta >= 0.8 && framesDelta >= 1;

          // 防卡死：若 timeDelta >= 2s 仍然 framesDelta=0（真卡顿），强制算作有效采样
          // 让历史曲线如实反映 "接收端连续 2 秒没帧" 这种真实故障
          const isForcedStall = timeDelta >= 2.0;

          if (isValidSample || isForcedStall) {
            const calculatedFps = framesDelta / timeDelta;
            const calculatedKbps = (bytesDelta * 8) / timeDelta / 1000;

            // 诊断日志：仅在异常时输出
            if ((calculatedFps === 0 || calculatedFps > 100) && !document.hidden) {
            }

            this._currentStats.fps = Math.round(calculatedFps);
            this._currentStats.bitrate = Math.round(calculatedKbps);

            // 每帧平均解码时间：仅在有效采样窗口内计算，和 fps/bitrate 语义对齐
            // （decode time 本身依赖 "这段时间新解码了多少帧"，无效采样时分母无意义）
            if (report.totalDecodeTime !== undefined && framesDelta > 0) {
              const decodeTimeDelta = report.totalDecodeTime - (this.prevStats.totalDecodeTime || 0);
              if (decodeTimeDelta >= 0) {
                const decodeTimePerFrameMs = (decodeTimeDelta * 1000) / framesDelta;
                this._currentStats.latencyBreakdown.decode = decodeTimePerFrameMs;
              }
              this.prevStats.totalDecodeTime = report.totalDecodeTime;
            }

            // 只在有效采样时才前移基准点
            this.fpsSample.framesDecoded = report.framesDecoded;
            this.fpsSample.bytesReceived = report.bytesReceived;
            this.fpsSample.timestamp = now;
          }
          // 无效采样：什么都不做，保留 _currentStats.fps/bitrate/decode 为上一次的值
        }

        // Jitter buffer delay - calculate average delay per frame in this interval
        if (
          report.jitterBufferDelay !== undefined &&
          report.jitterBufferEmittedCount > 0
        ) {
          const delayDelta = report.jitterBufferDelay - this.prevStats.jitterBufferDelay;
          const countDelta = report.jitterBufferEmittedCount - this.prevStats.jitterBufferEmittedCount;

          // 至少 1 帧就更新；低 fps 时也允许更新，避免值被冻结
          if (countDelta >= 1) {
            const jitterMs = (delayDelta / countDelta) * 1000;

            // CRITICAL: Filter out obviously invalid values
            // jitterBufferDelay is cumulative, so division errors can produce huge values
            // Max reasonable jitter buffer is ~500ms (half second of buffering)
            if (jitterMs >= 0 && jitterMs <= 500) {
              // Apply exponential smoothing (alpha = 0.2 for more stability)
              if (this.smoothedJitterBuffer === 0) {
                this.smoothedJitterBuffer = jitterMs;
              } else {
                this.smoothedJitterBuffer = 0.2 * jitterMs + 0.8 * this.smoothedJitterBuffer;
              }

              this._currentStats.latencyBreakdown.jitterBuffer = this.smoothedJitterBuffer;
              this.staleJitterTicks = 0;

              // Track consecutive high readings
              if (this.smoothedJitterBuffer > 50) {
                this.consecutiveHighCount++;
                if (this.consecutiveHighCount % 5 === 1) { // Log every 5th
                }
              } else {
                this.consecutiveHighCount = 0;
              }
            } else if (jitterMs > 500) {
              // Log but don't use invalid values - likely calculation error
              console.warn(`[Stats] Ignoring invalid jitter buffer: ${jitterMs.toFixed(1)}ms (delayDelta=${delayDelta.toFixed(3)}, countDelta=${countDelta})`);
            }
          } else {
            // 连续无新解码帧时衰减旧值，避免历史曲线被卡成一条直线
            this.staleJitterTicks++;
            if (this.staleJitterTicks >= 3 && this.smoothedJitterBuffer > 0) {
              this.smoothedJitterBuffer *= 0.7;
              if (this.smoothedJitterBuffer < 0.1) this.smoothedJitterBuffer = 0;
              this._currentStats.latencyBreakdown.jitterBuffer = this.smoothedJitterBuffer;
            }
          }
          this.prevStats.jitterBufferDelay = report.jitterBufferDelay;
          this.prevStats.jitterBufferEmittedCount = report.jitterBufferEmittedCount;
        }

        // Other stats
        this._currentStats.jitter = (report.jitter || 0) * 1000;
        this._currentStats.framesDecoded = report.framesDecoded || 0;
        if (!this.hasEverDecodedFrame_ && (report.framesDecoded || 0) > 0) {
          this.hasEverDecodedFrame_ = true;  // 出过帧后死锁检测自动切宽容路径，不会打断慢网正在补齐的首帧
        }
        this._currentStats.framesReceived = report.framesReceived || 0;
        this._currentStats.framesDropped = report.framesDropped || 0;
        this._currentStats.keyframesDecoded = report.keyFramesDecoded || 0;
        this._currentStats.packetsReceived = report.packetsReceived || 0;
        this._currentStats.nackCount = report.nackCount || 0;
        this._currentStats.pliCount = report.pliCount || 0;
        this._currentStats.freezeCount = report.freezeCount || 0;
        this._currentStats.totalFreezesDuration = report.totalFreezesDuration || 0;

        // decode time per frame 的计算已合并进上方"有效采样"分支（和 fps/bitrate 共用
        // framesDelta，语义一致、无效采样时保留上一次值）。此处不再单独计算。

        // Packet loss (Instantaneous with smoothing)
        const packetsLost = report.packetsLost || 0;
        const packetsReceived = report.packetsReceived || 0;

        let deltaLost = packetsLost - this.prevStats.packetsLost;
        let deltaReceived = packetsReceived - this.prevStats.packetsReceived;

        // After ICE restart, counters may reset - ignore negative deltas
        if (deltaLost < 0) deltaLost = 0;
        if (deltaReceived < 0) deltaReceived = 0;

        // Only calculate packet loss if we have enough samples (at least 10 packets)
        // When FPS is low, small sample sizes cause erratic loss percentages
        if (deltaReceived + deltaLost >= 10) {
          const instantLoss = (deltaLost / (deltaReceived + deltaLost)) * 100;
          const prevLoss = this._currentStats.packetLoss;
          // Spike 过滤：一次 instantLoss > 10% 且 > 10 倍上一稳定值，认为是
          // ICE candidate 切换 / inbound-rtp 对象重建导致的计数对齐异常，丢弃本次不入 EWMA。
          // 避免图表出现"突然跳 50%，然后靠 0.7 系数拖尾 15 秒才恢复"的假象。
          // 阈值 10% / 10x 选择理由：真实网络即便突发拥塞，单次 1 秒窗口内跳到 10 倍
          // 前值且绝对值 > 10% 的情况极少——若出现，通常会伴随 freeze/pli，下次有效
          // 丢包还会再触发，不会永久被过滤掉。
          const isSpike = instantLoss > 10 && prevLoss > 0 && instantLoss > 10 * prevLoss;
          if (isSpike) {
            console.warn(`[Stats] Drop spike packetLoss: ${instantLoss.toFixed(2)}% (prev=${prevLoss.toFixed(3)}%, deltaLost=${deltaLost}, deltaReceived=${deltaReceived})`);
          } else {
            // Apply smoothing to avoid sudden spikes
            this._currentStats.packetLoss = 0.3 * instantLoss + 0.7 * prevLoss;
          }
        } else if (deltaReceived + deltaLost > 0) {
          // For small samples, use more conservative smoothing
          const instantLoss = (deltaLost / (deltaReceived + deltaLost)) * 100;
          this._currentStats.packetLoss = 0.1 * instantLoss + 0.9 * this._currentStats.packetLoss;
        }
        // 浮点 EWMA 长尾归零：低于 1e-4% 视为 0，避免诊断报告出现 5e-15 这类噪音
        if (this._currentStats.packetLoss < 1e-4) this._currentStats.packetLoss = 0;

        // Update previous values
        // 注意：bytesReceived / framesDecoded / timestamp 的基准点改由 fpsSample 维护
        //      （见上方 "累计差分 + 有效采样门限" 注释），此处不再更新。
        //      保留 prevStats 类型定义是为了最小化改动面，可在后续清理中移除。
        this.prevStats.packetsLost = packetsLost;
        this.prevStats.packetsReceived = packetsReceived;
      }

      if (report.type === 'candidate-pair' && report.state === 'succeeded') {
        // Use 'selected' property to identify the actively used candidate pair
        // 'selected' indicates this pair is currently being used for data transport
        if (report.selected || (!selectedLocalCandidateId && (report.bytesSent || 0) + (report.bytesReceived || 0) > 0)) {
          // RTT
          this._currentStats.rtt = Math.round((report.currentRoundTripTime || 0) * 1000);
          this._currentStats.latencyBreakdown.network = this._currentStats.rtt;

          // Store candidate pair IDs for transport type detection
          selectedLocalCandidateId = report.localCandidateId;
          selectedRemoteCandidateId = report.remoteCandidateId;
        }
      }

      if (report.type === 'local-candidate') {
        // For relay candidates, relayProtocol field contains the TURN connection protocol (tcp/udp)
        // protocol field is the protocol of the candidate itself (usually udp for relay)
        const turnProtocol = report.relayProtocol || '';
        localCandidates.set(report.id, {
          type: report.candidateType || 'unknown',
          protocol: report.protocol || '',
          relayProtocol: turnProtocol,
          url: report.url || '',  // TURN server URL (e.g. turn:rtc2.03kan.com:3400?transport=udp)
        });
      }

      if (report.type === 'remote-candidate') {
        remoteCandidates.set(report.id, {
          type: report.candidateType || 'unknown',
          address: report.address || '',
          protocol: report.protocol || '',
        });
      }
    });

    // After iteration, look up the selected candidates
    const localCandidate = selectedLocalCandidateId
      ? localCandidates.get(selectedLocalCandidateId)
      : undefined;
    const localCandidateType = localCandidate?.type || 'unknown';
    const remoteCandidate = selectedRemoteCandidateId
      ? remoteCandidates.get(selectedRemoteCandidateId)
      : undefined;
    const remoteCandidateType = remoteCandidate?.type || 'unknown';

    // Update stats with selected candidate info
    this._currentStats.localCandidateType = localCandidateType;
    this._currentStats.remoteCandidateType = remoteCandidateType;
    this._currentStats.remoteAddress = remoteCandidate?.address || '';
    // Store protocol for relay connections (TCP/UDP)
    // For relay candidates, use relayProtocol field (TURN connection protocol)
    // Fall back to candidate protocol if relayProtocol is not available
    const turnProtocol = localCandidate?.relayProtocol?.toUpperCase() || '';
    const localProtocol = localCandidate?.protocol?.toUpperCase() || '';
    const remoteProtocol = remoteCandidate?.protocol?.toUpperCase() || '';

    if (this._knownTurnProtocol) {
      // Use the known TURN protocol from ICE Racing configuration
      this._currentStats.relayProtocol = this._knownTurnProtocol;
    } else if (turnProtocol) {
      // relayProtocol is the TURN connection protocol (tcp/udp)
      this._currentStats.relayProtocol = turnProtocol;
    } else if (localProtocol === 'TCP' || remoteProtocol === 'TCP') {
      this._currentStats.relayProtocol = 'TCP';
    } else {
      this._currentStats.relayProtocol = localProtocol || remoteProtocol || '';
    }

    // Determine transport type based on selected candidate pair
    // If either local or remote candidate is relay, it's a relay connection
    if (localCandidateType === 'relay' || remoteCandidateType === 'relay') {
      this._currentStats.transportType = 'relay';
      // 从实际使用的 local-candidate 的 url 字段提取 TURN 服务器域名
      const turnUrl = localCandidate?.url || '';
      if (turnUrl) {
        try {
          const parsed = new URL(turnUrl.replace(/^turns?:/, 'http://'));
          this._currentStats.relayServer = parsed.hostname;
        } catch { /* ignore */ }
      }
    } else if (localCandidateType !== 'unknown' || remoteCandidateType !== 'unknown') {
      this._currentStats.transportType = 'p2p';
      this._currentStats.relayServer = '';
    }

    // Calculate total latency
    const breakdown = this._currentStats.latencyBreakdown;
    // Stage 2B.7: total 现在包含 capture（host 端 capture_to_encode_lag_us_p95），更接近真实端到端延迟
    breakdown.total = breakdown.capture + breakdown.encode + breakdown.network + breakdown.jitterBuffer + breakdown.decode;
  }

  /**
   * 计算 30s 事件增量（host 推累计值，前端做差值）
   * 找出 ~30 秒前的 snapshot，当前减去它即为增量
   */
  getEvents30s(): NonNullable<ServerStats['events_total']> {
    const empty = {
      bwe_drop_30plus: 0, vbv_switch: 0, sudden_drop: 0,
      cpu_overload: 0, keyframe_forced: 0, encoder_recovery: 0,
      large_frame: 0, frames_skipped_bwe: 0, frames_skipped_interval_guard: 0,
    };
    // 优先用 events_lifetime（语义清晰），fallback events_total（旧 host 兼容）
    const cur = this._serverStats?.events_lifetime ?? this._serverStats?.events_total;
    if (!cur || this._eventsHistory.length === 0) return empty;

    const now = Date.now();
    // 找最早的 ≥30s 之前的 snapshot；若不足 30s 则用最早的
    let baseline = this._eventsHistory[0].events;
    for (const item of this._eventsHistory) {
      if (now - item.ts >= 30000) {
        baseline = item.events;
      } else {
        break;
      }
    }
    return {
      bwe_drop_30plus: Math.max(0, cur.bwe_drop_30plus - baseline.bwe_drop_30plus),
      vbv_switch: Math.max(0, cur.vbv_switch - baseline.vbv_switch),
      sudden_drop: Math.max(0, cur.sudden_drop - baseline.sudden_drop),
      cpu_overload: Math.max(0, cur.cpu_overload - baseline.cpu_overload),
      keyframe_forced: Math.max(0, cur.keyframe_forced - baseline.keyframe_forced),
      encoder_recovery: Math.max(0, cur.encoder_recovery - baseline.encoder_recovery),
      large_frame: Math.max(0, cur.large_frame - baseline.large_frame),
      frames_skipped_bwe: Math.max(0, cur.frames_skipped_bwe - baseline.frames_skipped_bwe),
      frames_skipped_interval_guard: Math.max(0, cur.frames_skipped_interval_guard - baseline.frames_skipped_interval_guard),
    };
  }

  /**
   * Handle server stats received via DataChannel
   */
  private handleServerStats(data: ServerStats): void {
    if (!this._serverStats) {
      // 首次收到时打一条日志，方便排查"加载服务端能力中..."占位是否会消失
    }
    // catalog 字段独立保存在 _encoderCatalog（不进 _serverStats），所以这里直接替换无副作用
    this._serverStats = data;
    // re-emit 给 UI（getter 会合并 catalog）。原 WebRTCService 直接 emit 'stats:serverStats' 的链路
    // 已经改成 emit 'stats:rawServerStats' 由本 service 接收，避免不带 catalog 的快照刷到 UI 引起闪烁。
    eventBus.emit('stats:serverStats', this.serverStats);

    // 入队事件 snapshot（保留最近 35s）— 优先 events_lifetime，fallback events_total
    const eventsSnapshot = data.events_lifetime ?? data.events_total;
    if (eventsSnapshot) {
      const now = Date.now();
      this._eventsHistory.push({ ts: now, events: { ...eventsSnapshot } });
      while (this._eventsHistory.length > 0 && now - this._eventsHistory[0].ts > 35000) {
        this._eventsHistory.shift();
      }
    }

    // Update current stats from server
    this._currentStats.resolution = `${data.width}x${data.height}`;
    this._currentStats.hostGpu = data.gpu_name || 'Unknown';
    this._currentStats.encoderName = data.encoder_name || data.encoder_type;
    this._currentStats.captureMode = data.capture_mode || 'Unknown';
    this._currentStats.hostPlatform = data.platform || '';
    this._currentStats.availableResolutions = data.available_resolutions || '';

    // Latency breakdown from server (convert us to ms)
    // Stage 2B.7 修订: capture 改用 p50（中位数，反映"日常采集→编码"间隔）
    // p95 容易被静态帧重发等离群值污染，留给 Dashboard 单独显示作为"最差情况"
    // 旧 capture_time_us 永远是 0（host 没填），fallback 保留兼容老 host
    if (data.capture_to_encode_lag_us_p50 !== undefined && data.capture_to_encode_lag_us_p50 > 0) {
      this._currentStats.latencyBreakdown.capture = data.capture_to_encode_lag_us_p50 / 1000;
    } else {
      this._currentStats.latencyBreakdown.capture = data.capture_time_us / 1000;
    }
    this._currentStats.latencyBreakdown.convert = data.convert_time_us / 1000;
    this._currentStats.latencyBreakdown.encode = data.encode_time_us / 1000;
  }

  /**
   * Handle server_caps received via DataChannel.
   *
   * server_caps 是小消息，承载 SettingsPanel 必需的能力字段
   * （encoder_type/encoder_name/av1_hw/cpu_cores/platform 等）。
   * 当 server_stats 因体积过大被 SCTP 静默丢弃时，server_caps 是这些字段的兜底来源。
   *
   * 合并到 _serverStats（不替换）— 如果之后 server_stats 也到达，同名字段会被覆盖为最新值。
   * 合并完成后 emit stats:serverStats，让订阅了该事件的 UI（SettingsPanel）触发重渲染。
   */
  private handleServerCaps(data: {
    encoder_type?: string;
    encoder_name?: string;
    gpu_name?: string;
    av1_hw?: boolean;
    cpu_cores?: number;
    cpu_threads?: number;
    capture_mode?: string;
    platform?: string;
  }): void {
    const wasNull = this._serverStats === null;
    if (wasNull) {
    }
    // 合并 caps 字段到 _serverStats（partial 对象，缺失字段保持现状）
    this._serverStats = { ...(this._serverStats as Partial<ServerStats>), ...data } as ServerStats;

    // 同步到 currentStats（SettingsPanel/StatsPanel 部分字段从这里读）
    if (data.encoder_name) this._currentStats.encoderName = data.encoder_name;
    else if (data.encoder_type) this._currentStats.encoderName = data.encoder_type;
    if (data.gpu_name) this._currentStats.hostGpu = data.gpu_name;
    if (data.capture_mode) this._currentStats.captureMode = data.capture_mode;
    if (data.platform) this._currentStats.hostPlatform = data.platform;

    // 触发 SettingsPanel 等订阅 stats:serverStats 的组件重渲染
    // 用 getter 返回合并 catalog 的快照（避免订阅方拿到不带 catalog 的旧 _serverStats）
    eventBus.emit('stats:serverStats', this.serverStats);
  }

  /**
   * Handle server_encoder_catalog（阶段 2）
   *
   * host 端启动期 probe 完成 / dirty / 5s 心跳 触发推送。
   * 把 catalog + active_encoder_id + probe_pending 合并到 _serverStats，
   * 现有订阅 stats:serverStats 的组件（SettingsPanel / Dashboard 等）无需新增订阅。
   *
   * payload 来自 WebRTCService 路由的 server_encoder_catalog DC 消息
   */
  private handleEncoderCatalog(data: {
    catalog?: import('../types').EncoderCatalogEntry[];
    active_encoder_id?: import('../types').EncoderId | 'none';
    probe_pending?: boolean;
  }): void {
    const wasEmpty = !this._encoderCatalog;
    if (wasEmpty) {
    }
    // 独立保存（与 _serverStats 解耦，server_stats 大消息每秒覆写也不影响）
    this._encoderCatalog = {
      catalog: data.catalog || [],
      active_encoder_id: data.active_encoder_id || 'none',
      probe_pending: !!data.probe_pending,
    };
    // 通知 UI：复用 stats:serverStats 事件，serverStats getter 已合并 catalog
    eventBus.emit('stats:serverStats', this.serverStats);
  }

  /**
   * Handle host version received via DataChannel
   */
  private handleHostVersion(data: { version: string }): void {
    if (data.version) {
      this._currentStats.hostVersion = data.version;
    }
  }

  /**
   * Handle PC switched (P2P assist seamless switch)
   * Updates the PC reference to the new P2P connection
   */
  private handlePcSwitched(data: { pc: RTCPeerConnection; stunServers: string[] }): void {
    this.pc = data.pc;

    // Reset prev stats to avoid incorrect rate calculations
    this.prevStats = {
      bytesReceived: 0,
      framesDecoded: 0,
      timestamp: 0,
      jitterBufferDelay: 0,
      jitterBufferEmittedCount: 0,
      packetsLost: 0,
      packetsReceived: 0,
      totalDecodeTime: 0,
    };
    this.fpsSample = { framesDecoded: 0, bytesReceived: 0, timestamp: 0 };

    // P2P 切换换 PC：复位 ever-decoded / framesReceived 基线，避免跨 PC 误判（reviewer 修正）
    this.hasEverDecodedFrame_ = false;
    this.bsLastFramesReceived_ = 0;

    // Force immediate poll to update transport type
    // 重要：先清除现有定时器，避免创建多个定时器链
    if (this.pollTimer) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.poll();
  }

  /**
   * Check for warning conditions
   */
  private checkWarnings(): void {
    // 高延迟：需连续超阈值(滤掉瞬时抖动) + 冷却(不每秒刷屏)
    if (this._currentStats.rtt > this.HIGH_LATENCY_THRESHOLD_MS) {
      this.highLatencyStreak++;
    } else {
      this.highLatencyStreak = 0;
    }
    if (this.highLatencyStreak >= this.HIGH_LATENCY_STREAK) {
      const nowMs = Date.now();
      if (nowMs - this.lastHighLatencyWarnAt >= this.HIGH_LATENCY_COOLDOWN_MS) {
        this.lastHighLatencyWarnAt = nowMs;
        eventBus.emit('stats:warning', {
          type: 'high_latency',
          message: `网络延迟过高: ${this._currentStats.rtt}ms`,
        });
      }
    }

    // 检测持续丢包 - 如果已经是 TURN 中继（不管 TCP/UDP），不需要切换
    const isRelay = this._currentStats.transportType === 'relay';
    const now = Date.now();

    // 初始化宽限期 - 前10秒丢包正常，跳过检测 (类似ABR的grace period)
    const inGracePeriod = this.connectionStartTime > 0 &&
                          (now - this.connectionStartTime) < this.INIT_GRACE_PERIOD_MS;

    if (!isRelay && !inGracePeriod) {
      if (this._currentStats.packetLoss >= this.PACKET_LOSS_THRESHOLD) {
        // 丢包率超过阈值
        if (this.packetLossStartTime === 0) {
          // 开始计时
          this.packetLossStartTime = now;
          const elapsed = ((now - this.connectionStartTime) / 1000).toFixed(1);
        } else if (now - this.packetLossStartTime >= this.PACKET_LOSS_DURATION_MS) {
          // 持续丢包超过5秒，触发切换到 TURN TCP
          const currentMode = this._currentStats.transportType === 'relay'
            ? `中转-${this._currentStats.relayProtocol || 'UDP'}`
            : 'P2P';
          eventBus.emit('stats:persistentPacketLoss', {
            packetLoss: this._currentStats.packetLoss,
            duration: now - this.packetLossStartTime,
          });
          // 重置计时器，避免重复触发
          this.packetLossStartTime = 0;
        }
      } else {
        // 丢包率恢复正常，重置计时
        if (this.packetLossStartTime !== 0) {
          this.packetLossStartTime = 0;
        }
      }
    }

    this.checkVideoStuck();
  }

  // 统一「视频卡死」检测（B0 中枢，单一检测器）：接收帧率 = 0 持续过久 → emit 'video:stuck'
  // 从 checkWarnings 抽出：低功耗模式（isEnabled=false）也必须保持检测，
  // 否则状态栏隐藏时 B4/PathObserver 的卡死信号链整体失明
  private checkVideoStuck(): void {
    const now = Date.now();
    // 双窗口：neverDecoded 用短窗口（FIRST_FRAME_GRACE 8s + STALL 5s）；已出帧用原值（30s grace + 15s）
    // 双冷却：lastFirstFrameReconnectTime_ / lastStallReconnectTime_（拆开，互不压制）
    // 过滤项：后台 tab（document.hidden）帧率天然为 0，不是死锁
    const isBackgroundTab = typeof document !== 'undefined' && document.hidden;
    const neverDecoded = !this.hasEverDecodedFrame_;
    const framesRecv = this._currentStats.framesReceived || 0;
    const hasIncomingData = framesRecv > this.bsLastFramesReceived_;
    this.bsLastFramesReceived_ = framesRecv;

    // connectionStartTime 由 start()（“ICE 已连通转 CONNECTED”时）设置 —— grace 从 ICE 连通起算
    // （reviewer 修正：不是从 race 开始；rank1 底座 2-4s 不在窗口内；窗口覆盖 ICE连通→首帧解码）
    const graceMs = neverDecoded ? this.FIRST_FRAME_GRACE_PERIOD_MS : this.DEADLOCK_GRACE_PERIOD_MS;
    const stallMs = neverDecoded ? this.FIRST_FRAME_STALL_DURATION_MS : this.ZERO_FPS_DURATION_MS;
    const cooldownMs = neverDecoded ? this.FIRST_FRAME_RECONNECT_COOLDOWN_MS : this.RECONNECT_COOLDOWN_MS;
    const lastReconnect = neverDecoded ? this.lastFirstFrameReconnectTime_ : this.lastStallReconnectTime_;

    const inGrace = this.connectionStartTime > 0 && (now - this.connectionStartTime) < graceMs;
    const inCooldown = lastReconnect > 0 && (now - lastReconnect) < cooldownMs;

    if (this._currentStats.fps === 0 && !isBackgroundTab && !inGrace && !inCooldown) {
      if (this.zeroFpsStartTime === 0) {
        this.zeroFpsStartTime = now;
      } else if (now - this.zeroFpsStartTime >= stallMs) {
        const duration = now - this.zeroFpsStartTime;
        // 分类 reason
        let reason: 'stall-after-decode' | 'codec-undecodable' | 'first-frame-stall';
        if (!neverDecoded) {
          reason = 'stall-after-decode';
        } else if (hasIncomingData && framesRecv > 0 && (this._currentStats.videoCodec || '').toLowerCase() !== 'h264') {
          reason = 'codec-undecodable';   // 有数据到达但解不出 + 当前非 h264 → codec 错配（#4）
        } else {
          reason = 'first-frame-stall';   // 没数据/TURN 黑洞 → 换路径（#3）
        }
        eventBus.emit('video:stuck', { reason, neverDecoded, duration, framesReceived: framesRecv, currentCodec: this._currentStats.videoCodec || 'auto' });
        if (neverDecoded) this.lastFirstFrameReconnectTime_ = now; else this.lastStallReconnectTime_ = now;
        this.zeroFpsStartTime = 0;
      }
    } else if (this._currentStats.fps > 0 && this.zeroFpsStartTime !== 0) {
      // fps 恢复了，重置计时
      this.zeroFpsStartTime = 0;
    }
  }

  // 低功耗模式的路径质量最小采样：从 getStats 结果提取 inbound-rtp video 的
  // 丢包/帧计数，喂给 PathObserver（丢包劣化）与 checkVideoStuck（卡死检测）。
  // 丢包增量与 full 模式共用 prevStats 计数基准，模式来回切换时增量不断裂。
  private samplePathQualityLowPower(stats: RTCStatsReport): void {
    stats.forEach((report) => {
      if (report.type !== 'inbound-rtp' || report.kind !== 'video') return;

      // fps 直接用浏览器提供的 framesPerSecond（无差分，5s 粒度下也准确）
      this._currentStats.fps = Math.round(report.framesPerSecond || 0);
      this._currentStats.framesDecoded = report.framesDecoded || 0;
      if (this._currentStats.framesDecoded > 0) {
        this.hasEverDecodedFrame_ = true;
      }
      this._currentStats.framesReceived = report.framesReceived || 0;
      this._currentStats.keyframesDecoded = report.keyFramesDecoded || 0;

      const packetsReceived = report.packetsReceived || 0;
      const packetsLost = report.packetsLost || 0;
      const deltaReceived = packetsReceived - this.prevStats.packetsReceived;
      const deltaLost = packetsLost - this.prevStats.packetsLost;
      if (deltaReceived < 0 || deltaLost < 0) {
        // 计数器回卷（换 PC/重连）：只重置基准，本拍不计算
      } else if (deltaReceived + deltaLost > 0) {
        const instantLoss = (deltaLost / (deltaReceived + deltaLost)) * 100;
        // 平滑系数同 full 模式主路径（0.3/0.7）；不带 spike 过滤——低功耗模式
        // 单拍覆盖 5s，样本量大，单点毛刺天然被窗口稀释
        this._currentStats.packetLoss = 0.3 * instantLoss + 0.7 * this._currentStats.packetLoss;
        if (this._currentStats.packetLoss < 1e-4) this._currentStats.packetLoss = 0;
      }
      this.prevStats.packetsLost = packetsLost;
      this.prevStats.packetsReceived = packetsReceived;
    });
  }

  // 路径质量事件：PathObserver 专用数据源（full / 低功耗两个分支都发射）。
  // 不复用 stats:update——低功耗模式下其余字段是旧值，不该唤醒其他监听者
  private emitPathQuality(): void {
    eventBus.emit('stats:pathQuality', {
      packetLoss: this._currentStats.packetLoss,
      framesReceived: this._currentStats.framesReceived,
      framesDecoded: this._currentStats.framesDecoded,
      keyframesDecoded: this._currentStats.keyframesDecoded,
    });
  }

  /**
   * Perform jitter buffer management
   * Note: playbackRate doesn't affect WebRTC jitter buffer (it's at decoder level)
   * Instead, we request keyframes to help reset the buffer
   */
  private performCatchup(): void {
    const jitterBuffer = this.smoothedJitterBuffer;

    // Send jitter buffer to server via DataChannel for adaptive bitrate
    this.sendJitterBufferToServer(jitterBuffer);

    // 主动请求关键帧的逻辑已移除：libwebrtc PLI 已经覆盖了解码失败场景，
    // 任何前端的"兜底定时请求"都会绕过 host throttler，在差网络下反而引发关键帧风暴。
    // 长期方案是开启编码器 Intra Refresh，让丢包能自然自愈。
  }

  /**
   * Send jitter buffer value to server for adaptive bitrate control
   */
  private sendJitterBufferToServer(jitterBufferMs: number): void {
    // Use eventBus to send via DataChannel
    eventBus.emit('stats:sendJitterBuffer', { jitterBuffer: jitterBufferMs });
  }

  /**
   * Send network stats to server for adaptive FPS control
   * Called every poll interval when enabled
   */
  private sendNetworkStatsToServer(): void {
    const stats = this._currentStats;

    // Only send if we have valid data
    if (stats.rtt <= 0 && stats.packetLoss <= 0 && stats.jitter <= 0) {
      return;
    }

    eventBus.emit('stats:sendNetworkStats', {
      rtt: stats.rtt,                                    // Round-trip time (ms)
      jitter: stats.jitter,                              // Jitter (ms)
      packetLoss: stats.packetLoss,                      // Packet loss (%)
      jitterBuffer: stats.latencyBreakdown.jitterBuffer, // Jitter buffer (ms)
      bitrate: stats.bitrate,                            // Current bitrate (kbps)
    });
  }

  /**
   * Update stats history
   */
  private updateHistory(): void {
    // Stage 3D: 60 → 300 (5 min × 1Hz)
    const maxLength = 300;

    const sv = this._serverStats;
    this.history.timestamps.push(Date.now());
    this.history.fps.push(this._currentStats.fps);
    this.history.bitrate.push(this._currentStats.bitrate);
    this.history.latency.push(this._currentStats.latencyBreakdown.total);
    this.history.packetLoss.push(this._currentStats.packetLoss);
    this.history.jitterBuffer.push(this._currentStats.latencyBreakdown.jitterBuffer);
    this.history.rtt.push(this._currentStats.rtt);
    // Host 逐秒帧计数
    this.history.hostCaptureFrames.push(sv?.sec_capture_frames ?? -1);
    this.history.hostEncodeFrames.push(sv?.sec_encode_frames ?? -1);
    this.history.hostDeliveredFrames.push(sv?.sec_delivered_frames ?? -1);
    // Stage 3D 新字段
    this.history.qp.push(sv?.encoder_qp ?? -1);
    this.history.encodeMs.push(this._currentStats.latencyBreakdown.encode);
    this.history.captureLagMs.push(this._currentStats.latencyBreakdown.capture);
    const util = (sv?.actual_encode_kbps && sv?.bitrate_kbps && sv.bitrate_kbps > 0)
      ? Math.round((sv.actual_encode_kbps / sv.bitrate_kbps) * 100)
      : 0;
    this.history.utilizationPct.push(util);

    // Trim to max length
    if (this.history.timestamps.length > maxLength) {
      this.history.timestamps = this.history.timestamps.slice(-maxLength);
      this.history.fps = this.history.fps.slice(-maxLength);
      this.history.bitrate = this.history.bitrate.slice(-maxLength);
      this.history.latency = this.history.latency.slice(-maxLength);
      this.history.packetLoss = this.history.packetLoss.slice(-maxLength);
      this.history.jitterBuffer = this.history.jitterBuffer.slice(-maxLength);
      this.history.rtt = this.history.rtt.slice(-maxLength);
      this.history.hostCaptureFrames = this.history.hostCaptureFrames.slice(-maxLength);
      this.history.hostEncodeFrames = this.history.hostEncodeFrames.slice(-maxLength);
      this.history.hostDeliveredFrames = this.history.hostDeliveredFrames.slice(-maxLength);
      this.history.qp = this.history.qp.slice(-maxLength);
      this.history.encodeMs = this.history.encodeMs.slice(-maxLength);
      this.history.captureLagMs = this.history.captureLagMs.slice(-maxLength);
      this.history.utilizationPct = this.history.utilizationPct.slice(-maxLength);
    }
  }

  /**
   * Get chart data for recharts
   * Stage 3D: 增加 qp/encodeMs/captureLagMs/utilizationPct 字段，5min ring
   * @param maxPoints 可选：限制返回点数（如 60 表示最近 60s）；不传返回全部 300 点
   */
  getChartData(maxPoints?: number): Array<{
    time: number; bitrate: number; fps: number;
    packetLoss: number; jitterBuffer: number; rtt: number;
    qp: number; encodeMs: number; captureLagMs: number; utilizationPct: number;
  }> {
    const sliceFrom = maxPoints && maxPoints < this.history.timestamps.length
      ? this.history.timestamps.length - maxPoints
      : 0;

    return this.history.timestamps.slice(sliceFrom).map((time, idx) => {
      const i = idx + sliceFrom;
      return {
        time,
        bitrate: this.history.bitrate[i],
        fps: this.history.fps[i],
        packetLoss: this.history.packetLoss[i],
        jitterBuffer: this.history.jitterBuffer[i],
        rtt: this.history.rtt[i],
        qp: this.history.qp[i],
        encodeMs: this.history.encodeMs[i],
        captureLagMs: this.history.captureLagMs[i],
        utilizationPct: this.history.utilizationPct[i],
      };
    });
  }

  /**
   * 旧 getChartData 签名（兼容现有调用方），返回最近 60 个点
   */
  getChartDataLegacy(): Array<{ time: number; bitrate: number; fps: number; packetLoss: number; jitterBuffer: number; rtt: number }> {
    return this.history.timestamps.slice(-60).map((time, idx) => {
      const i = this.history.timestamps.length - Math.min(60, this.history.timestamps.length) + idx;
      return {
        time,
        bitrate: this.history.bitrate[i],
        fps: this.history.fps[i],
        packetLoss: this.history.packetLoss[i],
        jitterBuffer: this.history.jitterBuffer[i],
        rtt: this.history.rtt[i],
      };
    });
  }

  /**
   * Generate diagnostic report
   */
  generateReport(): string {
    const s = this._currentStats;
    const sv = this._serverStats;
    const hostRelay = formatHostRelay(sv);
    const report: Record<string, unknown> = {
      // === 管线分段：一眼看出哪个环节有问题 ===
      管线: {
        采集: {
          采集模式: sv?.capture_mode ?? s.captureMode,
          采集帧率: sv?.capture_fps ?? 0,
          目标帧率: sv?.target_fps ?? 0,
          静态帧占比: sv?.static_frame_ratio ?? 0,
          采集失败次数: sv?.capture_fail_count ?? 0,
        },
        编码: {
          编码器: sv?.encoder_name ?? s.encoderName,
          编码格式: s.videoCodec,
          BWE目标码率kbps: sv?.bitrate_kbps ?? 0,
          实际编码码率kbps: sv?.actual_encode_kbps ?? 0,
          编码帧率: sv?.encode_fps ?? 0,
          累计编码帧数: sv?.frames_encoded ?? 0,
          编码耗时ms: s.latencyBreakdown.encode,
        },
        网络: {
          'RTT延迟ms': s.rtt,
          '丢包率%': s.packetLoss,
          抖动ms: s.jitter,
          传输类型: s.transportType,
          本地候选类型: s.localCandidateType,
          远程候选类型: s.remoteCandidateType,
          远程地址: s.remoteAddress,
          中继协议: s.relayProtocol,
          '浏览器中继服务器': s.relayServer || '',
          'Host中继服务器': hostRelay,
          'Host当前路径': sv?.host_active_path || '',
          'Host当前会话': sv?.host_active_session || '',
        },
        接收: {
          接收帧率: s.fps,
          接收码率kbps: s.bitrate,
          累计解码帧: s.framesDecoded,
          累计接收帧: s.framesReceived,
          累计丢弃帧: s.framesDropped,
          关键帧数: s.keyframesDecoded,
          累计接收包: s.packetsReceived,
          NACK次数: s.nackCount,
          PLI次数: s.pliCount,
          冻结次数: s.freezeCount,
          总冻结时长ms: s.totalFreezesDuration,
        },
      },
      主机: {
        GPU: sv?.gpu_name ?? s.hostGpu,
        CPU: sv?.cpu_model ?? 'Unknown',
        'CPU核心': sv?.cpu_cores ? `${sv.cpu_cores}核${sv.cpu_threads}线程` : 'Unknown',
        'CPU占用': sv?.cpu_usage != null ? `${sv.cpu_usage}%` : 'Unknown',
        分辨率: `${sv?.width ?? 0}x${sv?.height ?? 0}`,
        平台: sv?.platform ?? s.hostPlatform,
        版本: s.hostVersion,
        可用分辨率: sv?.available_resolutions ?? s.availableResolutions,
      },
      延迟分解: {
        采集: s.latencyBreakdown.capture,
        转换: s.latencyBreakdown.convert,
        编码: s.latencyBreakdown.encode,
        网络: s.latencyBreakdown.network,
        抖动缓冲: s.latencyBreakdown.jitterBuffer,
        解码: s.latencyBreakdown.decode,
        总延迟: s.latencyBreakdown.total,
      },
      // === 历史数据 ===
      帧率历史: this.history.fps.slice(),
      码率历史: this.history.bitrate.slice(),
      RTT历史: this.history.rtt.slice(),
      丢包率历史: this.history.packetLoss.slice(),
      抖动缓冲历史: this.history.jitterBuffer.slice(),
      // Host 逐秒帧计数（与帧率历史对齐，用于定位 FPS 掉零的环节）
      主机采集帧历史: this.history.hostCaptureFrames.slice(),
      主机编码帧历史: this.history.hostEncodeFrames.slice(),
      主机投递帧历史: this.history.hostDeliveredFrames.slice(),
    };

    // 自定义序列化：数组单行显示
    const json = JSON.stringify(report, null, 2);
    return json.replace(
      /\[\s*\n\s*([\d\s,.\-eE]+)\s*\n\s*\]/g,
      (_match, inner: string) => '[' + inner.replace(/\s+/g, ' ').trim() + ']'
    );
  }

  /**
   * Get empty stats object
   */
  private getEmptyStats(): StreamStats {
    return {
      fps: 0,
      rtt: 0,
      bitrate: 0,
      packetLoss: 0,
      jitter: 0,
      resolution: '0x0',
      hostGpu: 'Unknown',
      encoderName: 'Unknown',
      captureMode: 'Unknown',
      videoCodec: 'H264',
      latencyBreakdown: {
        capture: 0,
        convert: 0,
        encode: 0,
        network: 0,
        jitterBuffer: 0,
        decode: 0,
        total: 0,
      },
      framesDecoded: 0,
      framesReceived: 0,
      framesDropped: 0,
      keyframesDecoded: 0,
      packetsReceived: 0,
      nackCount: 0,
      pliCount: 0,
      freezeCount: 0,
      totalFreezesDuration: 0,
      transportType: 'unknown',
      localCandidateType: 'unknown',
      remoteCandidateType: 'unknown',
      remoteAddress: '',
      relayProtocol: '',
      relayServer: '',
      hostVersion: '',
      hostPlatform: '',
      availableResolutions: '',
    };
  }
}

// Singleton instance
export const statsService = new StatsService();
export default statsService;
